import { useCallback, useContext, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import GenericTable from '../table/GenericTable'
import { ReportingContext } from '../../../../../wrappers/ReportingContext'
import { GroupByColumnsContext } from './GroupByColumnsContext'
import { useEffect } from 'react'
import GroupByColumnsTableViewOnly from './GroupByColumnsTableViewOnly'
import deepEqual from 'deep-equal'

const GroupByColumns = [
    {
        Header: "In SELECT?",
        accessor: "inSelect",
        Cell: ({ row: { original } }) => {
            const { reportConfig } = useContext(ReportingContext)
            const columnsInSelect = useMemo(() => {
                return reportConfig && reportConfig.columns
                    ? reportConfig.columns.map(c => c.sourceColumn)
                    : []
            }, [reportConfig])
            
            return columnsInSelect.length === 0 || columnsInSelect.includes(original.sourceColumn) ? 'Yes' : 'No'
        },
    },
    {
        Header: 'Source.Column',
        accessor: 'sourceColumn',
        Cell: ({ value: initialValue, row: { index } }) => {
            const { register } = useFormContext()
            return <>
                {initialValue && (
                    <span>
                        {initialValue.split('.')[0]}.<b>{initialValue.split('.')[1]}</b>
                    </span>
                )}
                <input 
                    name={`groupBy[${index}].sourceColumn`} 
                    ref={register()} 
                    disabled
                    className={`collapsedField`} 
                    defaultValue={initialValue} 
                />
            </>
        },
    },
    {
        Header: 'Column Type',
        accessor: 'dataType',
        Cell: ({ value: initialValue, row: { index } }) => {
            const { register } = useFormContext()
            return <>
                {initialValue}
                <input 
                    name={`groupBy[${index}].dataType`} 
                    ref={register()} 
                    disabled
                    className={`collapsedField`} 
                    defaultValue={initialValue} 
                />
            </>
        },
    },
]

const GroupByColumnsTable = () => {
    const { formState, setValue } = useFormContext()
    const { fields, append, remove, move } = useFieldArray({
        name: 'groupBy'
    })

    const { resetForm, reportConfig } = useContext(ReportingContext)
    const { setRightTableData, 
        rightTableHighlights,
        setRightTableHighlights, setRightTableFunctions } = useContext(GroupByColumnsContext)
    const [formDirty, setFormDirty] = useState(formState.dirty)

    useEffect(() => {
        setRightTableFunctions(prev => {
            return { ...prev, append: append, remove: remove }
        })
    }, [append, remove, setRightTableFunctions])

    useEffect(() => {
        setRightTableData(fields)
    }, [fields, setRightTableData])

    const [moveMode, setMoveMode] = useState(false)
    useEffect(() => {
        const fieldsWithoutId = fields.map(({ id, ...rest }) => rest);
        const hasMovedColumns = !deepEqual(fieldsWithoutId, reportConfig.groupBy)

        if (moveMode && hasMovedColumns) {
            setValue("groupBy", fieldsWithoutId, { shouldDirty: true })
        }

        const shouldMarkDirty =
            (!moveMode && formState.dirty) ||
            (moveMode && hasMovedColumns)

        setFormDirty(shouldMarkDirty)
    }, [fields, moveMode, formState.dirty, reportConfig.groupBy, setValue])
    
    const onMoveToggle = useCallback((moveEnabled, setMoveEnabled, setShowConfirmationModal) => {
        if (formDirty) {
            setShowConfirmationModal(true)                       
        } else {
            setMoveEnabled(prev => !prev)
            setMoveMode(prev => !prev)
        }
    }, [setMoveMode, formDirty])

    return (<>
        {!moveMode
            ? (
                <GenericTable
                    columns={GroupByColumns}
                    fields={fields}
                    tableHeight={'66vh'}
                    tableTile={'GROUP BY Columns'}
                    viewOnly={false}
                    onHightlight={setRightTableHighlights}
                    highlightedRows={rightTableHighlights}
                    filterable={false}
                    onMoveToggle={onMoveToggle}
                    formProps={{
                        formState,
                        resetForm
                    }}
                    noDataMessage='No columns selected; the report will not be grouped by any columns.'
                />
            ) : (
                <GenericTable
                    columns={GroupByColumnsTableViewOnly}
                    fields={fields}
                    tableHeight={'66vh'}
                    tableTile={'GROUP BY Columns'}
                    viewOnly={true}
                    onHightlight={setRightTableHighlights}
                    highlightedRows={rightTableHighlights}
                    filterable={false}
                    onMove={move}
                    onMoveToggle={onMoveToggle}
                    formProps={{
                        formState,
                        formDirty,
                        resetForm,
                    }}
                    noDataMessage='No columns selected; the report will not be grouped by any columns.'
                />
            )
        }
    </>)
}

export default GroupByColumnsTable
